import { http } from "./client";
import type { PageParams } from "@/types/api";
import type {
  PerformanceAssignResult,
  PerformanceBulkGoalResult,
  PerformanceCycle,
  PerformanceCycleEmployee,
  PerformanceCycleStats,
  PerformanceEmployeeScoreRow,
  PerformanceFeedbackEntry,
  PerformanceFeedbackEntryKind,
  PerformanceFeedbackVisibility,
  PerformanceFinalPreview,
  PerformanceGoalApproval,
  PerformanceGoalType,
  PerformanceItemKind,
  PerformanceKPITemplate,
  PerformancePIPCheckIn,
  PerformancePIPCheckInStatus,
  PerformancePIPProgress,
  PerformanceRecognition,
  PerformanceRecommendation,
  PerformanceRecommendationStatus,
  PerformanceRecommendationType,
  PerformanceScoringMethod,
  PerformanceTrendPoint,
  PerformanceCycleStatus,
  PerformanceCycleType,
  PerformanceDashboard,
  PerformanceDevelopmentActivity,
  PerformanceDevelopmentActivityStatus,
  PerformanceDevelopmentActivityType,
  PerformanceDevelopmentPlan,
  PerformanceDevelopmentPlanStatus,
  PerformanceFeedback,
  PerformanceFeedbackRequest,
  PerformanceFeedbackRequestStatus,
  PerformanceFeedbackReviewerType,
  PerformanceGoal,
  PerformanceGoalCategory,
  PerformanceGoalPriority,
  PerformanceGoalStatus,
  PerformancePIP,
  PerformancePIPAction,
  PerformancePIPActionStatus,
  PerformancePIPReviewFrequency,
  PerformancePIPStatus,
  PerformanceRatingLevel,
  PerformanceRatingScale,
  PerformanceReview,
  PerformanceReviewOverallStatus,
  PerformanceSkillGap,
} from "@/types/domain";

export interface PerformanceCycleFilters extends PageParams {
  status?: PerformanceCycleStatus | "";
  cycle_type?: PerformanceCycleType | "";
  /** Name search. */
  q?: string;
}

export interface PerformanceCycleInput {
  name?: string;
  description?: string;
  cycle_type?: PerformanceCycleType;
  start_date?: string;
  end_date?: string;
  self_review_start?: string;
  self_review_deadline?: string;
  manager_review_start?: string;
  manager_review_deadline?: string;
  rating_scale_id?: string;
  goal_weight?: number;
  manager_weight?: number;
  feedback_weight?: number;
  competency_weight?: number;
  require_full_goal_weightage?: boolean;
  hr_review_enabled?: boolean;
  competency_fallback?: "manager_rating" | "exclude";
}

export interface PerformanceGoalFilters extends PageParams {
  cycle_id?: string;
  employee_id?: string;
  department_id?: string;
  status?: PerformanceGoalStatus | "";
  category?: PerformanceGoalCategory | "";
  goal_type?: PerformanceGoalType | "";
  item_kind?: PerformanceItemKind | "";
  approval_status?: PerformanceGoalApproval | "";
  /** Title search. */
  q?: string;
  overdue?: boolean;
  awaiting_verification?: boolean;
}

export interface PerformanceGoalInput {
  cycle_id?: string;
  employee_id?: string;
  title?: string;
  description?: string;
  category?: PerformanceGoalCategory;
  weightage?: number;
  target_value?: number;
  unit?: string;
  start_date?: string;
  due_date?: string;
  priority?: PerformanceGoalPriority;
  auto_progress_from_tasks?: boolean;
  item_kind?: PerformanceItemKind;
  scoring_method?: PerformanceScoringMethod;
}

export interface PerformanceBulkGoalInput extends PerformanceGoalInput {
  goal_type: Exclude<PerformanceGoalType, "individual">;
  employee_ids?: string[];
  department_id?: string;
  all?: boolean;
}

export interface PerformanceKPITemplateInput {
  name?: string;
  description?: string;
  item_kind?: PerformanceItemKind;
  category?: PerformanceGoalCategory;
  target_value?: number;
  unit?: string;
  weightage?: number;
  scoring_method?: PerformanceScoringMethod;
  department_id?: string;
  designation?: string;
  is_active?: boolean;
}

export interface PerformanceAssignInput {
  employee_ids?: string[];
  department_id?: string;
  branch_id?: string;
  designation?: string;
  all?: boolean;
}

export interface PerformanceRecommendationInput {
  employee_id: string;
  cycle_id?: string;
  review_id?: string;
  recommendation_type: PerformanceRecommendationType;
  proposed_detail?: string;
  justification: string;
}

export interface PerformanceRecommendationFilters extends PageParams {
  employee_id?: string;
  cycle_id?: string;
  status?: PerformanceRecommendationStatus | "";
  recommendation_type?: PerformanceRecommendationType | "";
}

export interface PerformanceFeedbackEntryInput {
  to_employee_id: string;
  kind: PerformanceFeedbackEntryKind;
  visibility: PerformanceFeedbackVisibility;
  competency?: string;
  message: string;
  goal_id?: string;
  cycle_id?: string;
}

export interface PerformancePIPCheckInInput {
  scheduled_date?: string;
  held_on?: string;
  status?: PerformancePIPCheckInStatus;
  overall_progress?: PerformancePIPProgress;
  progress_summary?: string;
  manager_notes?: string;
  employee_comments?: string;
  next_steps?: string;
}

export interface PerformanceEmployeeScoreFilters extends PageParams {
  cycle_id: string;
  department_id?: string;
  branch_id?: string;
  manager_id?: string;
  stage?: "self_review" | "manager_review" | "completed" | "overdue" | "";
  q?: string;
  sort?: "name" | "department" | "stage" | "final_score" | "goal_progress";
  order?: "asc" | "desc";
}

export interface PerformanceReviewFilters extends PageParams {
  cycle_id?: string;
  employee_id?: string;
  status?: PerformanceReviewOverallStatus | "";
}

export interface PerformanceSelfReviewInput {
  accomplishments?: string;
  challenges?: string;
  strengths?: string;
  improvements?: string;
  training_needs?: string;
  career_aspirations?: string;
  comments?: string;
  rating_level_id?: string;
}

export interface PerformanceManagerReviewInput {
  strengths?: string;
  improvements?: string;
  comments?: string;
  recommendation?: string;
  promotion_recommended?: boolean;
  training_recommended?: string;
  rating_level_id?: string;
}

export interface PerformanceFinalEvaluationInput {
  hr_comments: string;
  key_achievements: string;
  competency_score?: number;
}

export interface PerformanceDevelopmentActivityOwnerInput {
  owner_id?: string;
  outcome?: string;
}

export interface PerformanceFeedbackRequestFilters extends PageParams {
  cycle_id?: string;
  employee_id?: string;
  status?: PerformanceFeedbackRequestStatus | "";
}

export interface PerformanceFeedbackRequestInput {
  cycle_id: string;
  employee_id: string;
  reviewer_id?: string;
  reviewer_type: PerformanceFeedbackReviewerType;
  custom_reviewer_name?: string;
  custom_reviewer_email?: string;
  is_anonymous?: boolean;
  deadline?: string;
}

export interface PerformanceFeedbackSubmitInput {
  ratings: Record<string, unknown>;
  overall_rating?: number;
  written_feedback?: string;
  strengths?: string;
  improvements?: string;
}

export interface PerformancePIPFilters extends PageParams {
  employee_id?: string;
  status?: PerformancePIPStatus | "";
}

export interface PerformancePIPInput {
  employee_id?: string;
  cycle_id?: string;
  reason?: string;
  performance_issues?: string;
  objectives?: string;
  success_criteria?: string;
  start_date?: string;
  end_date?: string;
  review_frequency?: PerformancePIPReviewFrequency;
  hr_owner_id?: string;
  manager_id?: string;
}

export interface PerformancePIPActionInput {
  action?: string;
  description?: string;
  owner_id?: string;
  due_date?: string;
  status?: PerformancePIPActionStatus;
  completion_percentage?: number;
  comments?: string;
}

export interface PerformanceDevelopmentPlanFilters extends PageParams {
  employee_id?: string;
  status?: PerformanceDevelopmentPlanStatus | "";
}

export interface PerformanceDevelopmentPlanInput {
  employee_id?: string;
  cycle_id?: string;
  career_goals?: string;
  skill_gaps?: PerformanceSkillGap[];
  status?: PerformanceDevelopmentPlanStatus;
  target_completion_date?: string;
  progress_percentage?: number;
}

export interface PerformanceDevelopmentActivityInput {
  activity_type?: PerformanceDevelopmentActivityType;
  title?: string;
  description?: string;
  provider?: string;
  target_date?: string;
  completion_date?: string;
  status?: PerformanceDevelopmentActivityStatus;
  progress_percentage?: number;
}

export interface PerformanceRatingScaleInput {
  name?: string;
  description?: string;
  is_active?: boolean;
  is_default?: boolean;
}

export interface PerformanceRatingLevelInput {
  scale_id?: string;
  level_order?: number;
  name?: string;
  min_score?: number;
  max_score?: number;
  description?: string;
}

export const performanceApi = {
  dashboard: () => http.get<PerformanceDashboard>("/performance/dashboard"),
  /** The dashboard for one chosen cycle (the picker on the dashboard page). */
  dashboardForCycle: (cycleId: string) => http.get<PerformanceDashboard>("/performance/dashboard", { cycle_id: cycleId }),

  // ---- cycles ----
  listCycles: (filters: PerformanceCycleFilters = {}) => http.getPage<PerformanceCycle>("/performance/cycles", filters),
  getCycle: (id: string) => http.get<PerformanceCycle>(`/performance/cycles/${id}`),
  createCycle: (input: PerformanceCycleInput) => http.post<PerformanceCycle>("/performance/cycles", input),
  updateCycle: (id: string, input: PerformanceCycleInput) => http.put<PerformanceCycle>(`/performance/cycles/${id}`, input),
  deleteCycle: (id: string) => http.delete(`/performance/cycles/${id}`),
  transitionCycle: (id: string, status: PerformanceCycleStatus, force = false) =>
    http.post<PerformanceCycle>(`/performance/cycles/${id}/transition`, { status, force }),
  assignCycle: (id: string, input: PerformanceAssignInput) =>
    http.post<PerformanceAssignResult>(`/performance/cycles/${id}/assign`, input),
  cycleProgress: (id: string, params: PageParams = {}) =>
    http.getPage<PerformanceCycleEmployee>(`/performance/cycles/${id}/progress`, params),
  myReview: (cycleId: string) => http.get<PerformanceReview>(`/performance/cycles/${cycleId}/my-review`),
  employeeReview: (cycleId: string, employeeId: string) =>
    http.get<PerformanceReview>(`/performance/cycles/${cycleId}/employees/${employeeId}/review`),

  // ---- goals ----
  listGoals: (filters: PerformanceGoalFilters = {}) => http.getPage<PerformanceGoal>("/performance/goals", filters),
  getGoal: (id: string) => http.get<PerformanceGoal>(`/performance/goals/${id}`),
  createGoal: (input: PerformanceGoalInput) => http.post<PerformanceGoal>("/performance/goals", input),
  updateGoal: (id: string, input: PerformanceGoalInput) => http.put<PerformanceGoal>(`/performance/goals/${id}`, input),
  updateGoalProgress: (
    id: string,
    input: {
      current_value?: number;
      progress_percentage?: number;
      status?: PerformanceGoalStatus;
      comment?: string;
      /** http(s) link to supporting evidence; validated server side. */
      evidence_url?: string;
    },
  ) => http.post<PerformanceGoal>(`/performance/goals/${id}/progress`, input),
  deleteGoal: (id: string) => http.delete(`/performance/goals/${id}`),
  /** An employee puts a goal forward for their manager's approval. */
  proposeGoal: (input: PerformanceGoalInput) => http.post<PerformanceGoal>("/performance/goals/propose", input),
  decideGoal: (id: string, decision: "approve" | "revise" | "reject", note?: string) =>
    http.post<PerformanceGoal>(`/performance/goals/${id}/decision`, { decision, note }),
  verifyGoal: (id: string, accept: boolean, note?: string) =>
    http.post<PerformanceGoal>(`/performance/goals/${id}/verify`, { accept, note }),
  setGoalApplicability: (id: string, applicable: boolean, reason?: string) =>
    http.post<PerformanceGoal>(`/performance/goals/${id}/applicability`, { applicable, reason }),
  /** One team / department / organization goal, given to many employees. */
  assignSharedGoal: (input: PerformanceBulkGoalInput) =>
    http.post<PerformanceBulkGoalResult>("/performance/goals/bulk", input),

  // ---- KPI / KRA templates ----
  listKPITemplates: (params: PageParams & { department_id?: string; designation?: string; q?: string; active?: boolean } = {}) =>
    http.getPage<PerformanceKPITemplate>("/performance/kpi-templates", params),
  createKPITemplate: (input: PerformanceKPITemplateInput) =>
    http.post<PerformanceKPITemplate>("/performance/kpi-templates", input),
  updateKPITemplate: (id: string, input: PerformanceKPITemplateInput) =>
    http.put<PerformanceKPITemplate>(`/performance/kpi-templates/${id}`, input),
  deleteKPITemplate: (id: string) => http.delete(`/performance/kpi-templates/${id}`),
  applyKPITemplate: (id: string, input: { cycle_id: string; employee_ids: string[]; due_date?: string }) =>
    http.post<PerformanceBulkGoalResult>(`/performance/kpi-templates/${id}/apply`, input),

  // ---- reviews ----
  listReviews: (filters: PerformanceReviewFilters = {}) => http.getPage<PerformanceReview>("/performance/reviews", filters),
  getReview: (id: string) => http.get<PerformanceReview>(`/performance/reviews/${id}`),
  updateSelfReview: (id: string, input: PerformanceSelfReviewInput) =>
    http.put<PerformanceReview>(`/performance/reviews/${id}/self`, input),
  submitSelfReview: (id: string) => http.post<PerformanceReview>(`/performance/reviews/${id}/self/submit`, {}),
  reopenSelfReview: (id: string, reason?: string) =>
    http.post<PerformanceReview>(`/performance/reviews/${id}/self/reopen`, { reason }),
  updateManagerReview: (id: string, input: PerformanceManagerReviewInput) =>
    http.put<PerformanceReview>(`/performance/reviews/${id}/manager`, input),
  submitManagerReview: (id: string) => http.post<PerformanceReview>(`/performance/reviews/${id}/manager/submit`, {}),
  approveManagerReview: (id: string) => http.post<PerformanceReview>(`/performance/reviews/${id}/manager/approve`, {}),
  releaseFinalReview: (id: string, input: PerformanceFinalEvaluationInput) =>
    http.post<PerformanceReview>(`/performance/reviews/${id}/final`, input),
  /** What releasing would produce, without saving anything. */
  previewFinalReview: (id: string, competencyScore?: number) =>
    http.post<PerformanceFinalPreview>(`/performance/reviews/${id}/final/preview`, { competency_score: competencyScore }),
  /** Manager (or HR) sends a submitted self appraisal back to the employee. */
  returnSelfReview: (id: string, reason: string) =>
    http.post<PerformanceReview>(`/performance/reviews/${id}/self/return`, { reason }),
  /** HR sends a submitted/approved manager review back to the manager. */
  returnManagerReview: (id: string, reason: string) =>
    http.post<PerformanceReview>(`/performance/reviews/${id}/manager/return`, { reason }),
  /** HR withdraws a published final review so it can be corrected. */
  reopenFinalReview: (id: string, reason: string) =>
    http.post<PerformanceReview>(`/performance/reviews/${id}/final/reopen`, { reason }),
  developmentPlanFromReview: (id: string) =>
    http.post<PerformanceDevelopmentPlan>(`/performance/reviews/${id}/development-plan`, {}),

  // ---- recommendations ----
  listRecommendations: (filters: PerformanceRecommendationFilters = {}) =>
    http.getPage<PerformanceRecommendation>("/performance/recommendations", filters),
  createRecommendation: (input: PerformanceRecommendationInput) =>
    http.post<PerformanceRecommendation>("/performance/recommendations", input),
  decideRecommendation: (id: string, endorse: boolean, note?: string) =>
    http.post<PerformanceRecommendation>(`/performance/recommendations/${id}/decision`, { endorse, note }),
  withdrawRecommendation: (id: string) =>
    http.post<PerformanceRecommendation>(`/performance/recommendations/${id}/withdraw`, {}),

  // ---- continuous feedback + recognition ----
  listFeedbackEntries: (params: PageParams & { employee_id?: string; given?: boolean } = {}) =>
    http.getPage<PerformanceFeedbackEntry>("/performance/feedback/entries", params),
  giveFeedback: (input: PerformanceFeedbackEntryInput) =>
    http.post<PerformanceFeedbackEntry>("/performance/feedback/entries", input),
  /** Colleague search for the feedback form (no employee.read needed). */
  colleagues: (q?: string) =>
    http.get<{ id: string; name: string; employee_code: string; designation: string }[]>("/performance/colleagues", { q }),
  listRecognitions: (employeeId: string) =>
    http.get<PerformanceRecognition[]>("/performance/recognitions", { employee_id: employeeId }),

  // ---- 360 feedback ----
  listFeedbackRequests: (filters: PerformanceFeedbackRequestFilters = {}) =>
    http.getPage<PerformanceFeedbackRequest>("/performance/feedback/requests", filters),
  myFeedbackTasks: (params: PageParams = {}) => http.getPage<PerformanceFeedbackRequest>("/performance/feedback/my-tasks", params),
  getFeedbackRequest: (id: string) => http.get<PerformanceFeedbackRequest>(`/performance/feedback/requests/${id}`),
  requestFeedback: (input: PerformanceFeedbackRequestInput) =>
    http.post<PerformanceFeedbackRequest>("/performance/feedback/requests", input),
  submitFeedback: (id: string, input: PerformanceFeedbackSubmitInput) =>
    http.post<PerformanceFeedback>(`/performance/feedback/requests/${id}/submit`, input),
  declineFeedback: (id: string, reason?: string) =>
    http.post<PerformanceFeedbackRequest>(`/performance/feedback/requests/${id}/decline`, { reason }),

  // ---- rating scales ----
  listRatingScales: () => http.get<PerformanceRatingScale[]>("/performance/rating-scales"),
  createRatingScale: (input: PerformanceRatingScaleInput) => http.post<PerformanceRatingScale>("/performance/rating-scales", input),
  updateRatingScale: (id: string, input: PerformanceRatingScaleInput) =>
    http.put<PerformanceRatingScale>(`/performance/rating-scales/${id}`, input),
  createRatingLevel: (scaleId: string, input: PerformanceRatingLevelInput) =>
    http.post<PerformanceRatingLevel>(`/performance/rating-scales/${scaleId}/levels`, input),
  updateRatingLevel: (id: string, input: PerformanceRatingLevelInput) =>
    http.put<PerformanceRatingLevel>(`/performance/rating-levels/${id}`, input),
  deleteRatingLevel: (id: string) => http.delete(`/performance/rating-levels/${id}`),

  // ---- PIPs ----
  listPIPs: (filters: PerformancePIPFilters = {}) => http.getPage<PerformancePIP>("/performance/pips", filters),
  getPIP: (id: string) => http.get<PerformancePIP>(`/performance/pips/${id}`),
  createPIP: (input: PerformancePIPInput) => http.post<PerformancePIP>("/performance/pips", input),
  updatePIP: (id: string, input: PerformancePIPInput) => http.put<PerformancePIP>(`/performance/pips/${id}`, input),
  transitionPIP: (id: string, status: PerformancePIPStatus, outcomeComments?: string, newEndDate?: string) =>
    http.post<PerformancePIP>(`/performance/pips/${id}/transition`, {
      status, outcome_comments: outcomeComments, new_end_date: newEndDate,
    }),
  createPIPCheckIn: (pipId: string, scheduledDate: string) =>
    http.post<PerformancePIPCheckIn>(`/performance/pips/${pipId}/check-ins`, { scheduled_date: scheduledDate }),
  updatePIPCheckIn: (id: string, input: PerformancePIPCheckInInput) =>
    http.put<PerformancePIPCheckIn>(`/performance/pip-checkins/${id}`, input),
  createPIPAction: (pipId: string, input: PerformancePIPActionInput) =>
    http.post<PerformancePIPAction>(`/performance/pips/${pipId}/actions`, input),
  updatePIPAction: (id: string, input: PerformancePIPActionInput) =>
    http.put<PerformancePIPAction>(`/performance/pip-actions/${id}`, input),

  // ---- development plans ----
  listDevelopmentPlans: (filters: PerformanceDevelopmentPlanFilters = {}) =>
    http.getPage<PerformanceDevelopmentPlan>("/performance/development-plans", filters),
  getDevelopmentPlan: (id: string) => http.get<PerformanceDevelopmentPlan>(`/performance/development-plans/${id}`),
  createDevelopmentPlan: (input: PerformanceDevelopmentPlanInput) =>
    http.post<PerformanceDevelopmentPlan>("/performance/development-plans", input),
  updateDevelopmentPlan: (id: string, input: PerformanceDevelopmentPlanInput) =>
    http.put<PerformanceDevelopmentPlan>(`/performance/development-plans/${id}`, input),
  createDevelopmentActivity: (planId: string, input: PerformanceDevelopmentActivityInput) =>
    http.post<PerformanceDevelopmentActivity>(`/performance/development-plans/${planId}/activities`, input),
  updateDevelopmentActivity: (id: string, input: PerformanceDevelopmentActivityInput) =>
    http.put<PerformanceDevelopmentActivity>(`/performance/development-activities/${id}`, input),

  // ---- reports ----
  reviewCompletionReport: (cycleId: string) =>
    http.get<PerformanceCycleStats>("/performance/reports/review-completion", { cycle_id: cycleId }),
  employeeScores: (filters: PerformanceEmployeeScoreFilters) =>
    http.getPage<PerformanceEmployeeScoreRow>("/performance/reports/employee-scores", filters),
  trends: (limit = 12) => http.get<PerformanceTrendPoint[]>("/performance/reports/trends", { limit }),
  employeeHistory: (employeeId: string, limit = 12) =>
    http.get<PerformanceTrendPoint[]>(`/performance/employees/${employeeId}/history`, { limit }),
  ratingDistributionReport: (cycleId?: string) =>
    http.get<{ rating_name: string; count: number }[]>("/performance/reports/rating-distribution", { cycle_id: cycleId }),
  departmentReport: (cycleId?: string) =>
    http.get<{ department_name: string; employee_count: number; average_score: number; completion_rate: number }[]>(
      "/performance/reports/department-performance",
      { cycle_id: cycleId },
    ),
  exportReport: (
    report:
      | "review-completion" | "rating-distribution" | "department-performance" | "goal-achievement"
      | "pips" | "development-plans" | "employee-scores",
    format: "csv" | "xlsx" | "pdf",
    query: Record<string, string | undefined> = {},
  ) => http.download(`/performance/reports/${report}`, { ...query, format }),
};
